
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Mail, Phone } from 'lucide-react';
import { useLanguage } from '@/hooks/use-language';
import { getGroupColorClasses, getLocalizedGroupName } from '@/utils/groupColors';

interface Member {
  id: number;
  name: string;
  phone: string;
  email: string;
  groups: string[];
  subgroups: string[];
  role: string;
  isAdmin?: boolean;
}

interface Group {
  id: string;
  name: string;
  members: Array<{
    id: string;
    name: string;
    phone: string;
    email: string;
    role: string;
    isAdmin?: boolean;
  }>;
}

interface AddMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: Group;
  allMembers: Member[];
  onAddMembers: (memberIds: number[], role: string) => void;
}

export function AddMemberDialog({ 
  open, 
  onOpenChange, 
  group, 
  allMembers, 
  onAddMembers 
}: AddMemberDialogProps) {
  const { language, t } = useLanguage();
  const [search, setSearch] = React.useState('');
  const [selectedMembers, setSelectedMembers] = React.useState<number[]>([]);
  const [selectedRole, setSelectedRole] = React.useState('Member');

  // Defensive defaults when group is not yet provided
  const safeGroup: Group = group || { id: '', name: '', members: [] } as Group;

  // Get members already in this group (defensive default)
  const existingMemberIds = (safeGroup.members || []).map(m => m.id);
  
  // Filter available members (not already in group)
  const availableMembers = allMembers.filter(member =>
    !existingMemberIds.includes(member.id.toString()) &&
    member.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleMemberToggle = (memberId: number) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSubmit = () => {
    if (selectedMembers.length > 0) {
      onAddMembers(selectedMembers, selectedRole);
      setSelectedMembers([]);
      setSelectedRole('Member');
      setSearch('');
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedMembers([]);
    setSelectedRole('Member');
    setSearch('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("groups.addMembersTo")} {getLocalizedGroupName(safeGroup.name, language)}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("placeholder.searchMembers")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>{t("groups.roleForSelected")}</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Member">Member</SelectItem>
                <SelectItem value="Coach">Coach</SelectItem>
                <SelectItem value="Captain">Captain</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selected Count */}
          {selectedMembers.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {t("groups.membersSelected", { count: selectedMembers.length })}
            </div>
          )}

          {/* Available Members List */}
          <div className="flex-1 overflow-y-auto space-y-2 max-h-96">
            {availableMembers.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">
                {search ? t("groups.noMembersFound") : t("groups.allMembersInGroup")}
              </p>
            ) : (
              availableMembers.map(member => (
                <div
                  key={member.id}
                  className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleMemberToggle(member.id)}
                >
                  <Checkbox
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => {}} // Controlled by parent div
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{member.name}</span>
                      {member.isAdmin && (
                        <Badge variant="admin" className="text-xs">
                          Admin
                        </Badge>
                      )}
                      {member.role && !member.isAdmin && (
                        <Badge variant="outline" className="text-xs">
                          {getLocalizedGroupName(member.role, language)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {member.phone}
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {member.email}
                      </div>
                    </div>
                    {member.subgroups.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.subgroups.map(subgroup => {
                          const colors = getGroupColorClasses(subgroup);
                          return (
                            <Badge 
                              key={subgroup} 
                              variant="outline" 
                              className="text-xs"
                              style={{
                                borderColor: `hsl(var(--group-${colors.variant}))`,
                                color: `hsl(var(--group-${colors.variant}))`
                              }}
                            >
                              {getLocalizedGroupName(subgroup, language)}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={selectedMembers.length === 0}
          >
            {t("groups.addMembers", { count: selectedMembers.length })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
